import { type ReactElement, useEffect, useMemo, useState } from "react";

import {
  useGetIsLegalEntity,
  useGetOwnerDetails,
  useGetTradingAccount,
  useMutationRequest,
} from "~/v2/api/hooks";
import type { Account, OwnerDetails } from "~/v2/api/rest/account";
import {
  type PreviousStepResponse,
  previousStepPatch,
} from "~/v2/api/rest/onboarding";
import {
  type UseCountryReturn,
  useCountry,
} from "../../../api/hooks/useCountry";
import { isFpslEligible } from "./fpslEligibility";
import {
  clearFpslPreference,
  getFpslPreference,
  setFpslPreference,
} from "./fpslPreference";
import One from "./steps/basic";
import Disclosures from "./steps/disclosures";
import Employment from "./steps/employment";
import FinancialProfile from "./steps/financial";
import KYC from "./steps/kyc";
import Three from "./steps/review";
import StockLending from "./steps/stockLending";
import Type from "./steps/type";

export type SessionData = {
  step: Step;
  visibleSteps: Step[];
  ready: boolean;
  account: Account;
  details: OwnerDetails;
  milestone: number;
  prev: (patch?: boolean, woof?: CallableFunction) => Promise<boolean>;
  next: () => boolean;
  completeFpslStep: (optIn: boolean) => void;
  rerender: () => void;
  country: UseCountryReturn;
  isLegalEntity: boolean;
};

export type StepProps = {
  session: SessionData;
};

export type Step = {
  last_onboarding_step: string;
  title: string;
  description: string;
  component: (props: StepProps) => ReactElement;
};

const stockLendingStep: Step = {
  last_onboarding_step: "financial_profile_step",
  title: "Stock Lending",
  description: "Enroll in the program",
  component: StockLending,
};

const disclosuresStep: Step = {
  last_onboarding_step: "financial_profile_step",
  title: "Disclosures",
  description: "Important disclosures",
  component: Disclosures,
};

const reviewStep: Step = {
  last_onboarding_step: "disclosures_step",
  title: "Review",
  description: "Check your information",
  component: Three,
};

export const steps: Step[] = [
  {
    last_onboarding_step: "account_type_step",
    title: "Account Type",
    description: "Choose an account type",
    component: Type,
  },
  {
    last_onboarding_step: "signup_step",
    title: "Basic Info",
    description: "Personal details",
    component: One,
  },
  {
    last_onboarding_step: "address_step",
    title: "Employment",
    description: "Your employment status",
    component: Employment,
  },
  {
    last_onboarding_step: "employment_step",
    title: "Financial Profile",
    description: "Your financial profile",
    component: FinancialProfile,
  },
  stockLendingStep,
  disclosuresStep,
  reviewStep,
  {
    last_onboarding_step: "confirmation_step",
    title: "KYC",
    description: "Verify your identity",
    component: KYC,
  },
];

export const useSession = (): SessionData => {
  const countryUtil = useCountry();

  // keep the request sequence
  const [seq, setSeq] = useState(0);
  const [fpslStepPassed, setFpslStepPassed] = useState(false);

  // account is needed for every step
  const { account, refetch: refetchAccount } = useGetTradingAccount();

  const { isLegalEntity } = useGetIsLegalEntity();

  // Details must always match the onboarding account. Do not retain data from
  // the previous query key while a different account's details are loading.
  const { details, isFetching: isDetailsFetching } = useGetOwnerDetails({
    accountID: account?.id ?? "",
  });

  const mutation = useMutationRequest<PreviousStepResponse, string>((id) =>
    previousStepPatch(id),
  );

  const getStep = (name: string) =>
    steps.find((_, index) => steps[index]?.last_onboarding_step === name);

  const getHasItem = (key: string) =>
    Boolean(window.localStorage?.getItem(key));

  const rerender = () => setSeq(seq + 1);

  // biome-ignore lint/correctness/useExhaustiveDependencies: getStep, getHasItem and mutation.mutate are recreated every render, and countryUtil.infos/canLimitedFund are methods on a per-render object, so listing them would rebuild this session payload on every render. countryUtil.isLoading is already listed as the signal that its data settled.
  const data = useMemo<Partial<SessionData>>(() => {
    // if any of these haven't loaded, we shouldn't populate the data
    if (!details || !account || countryUtil.isLoading) {
      return {};
    }

    // make a clone of the account we can modify
    const clone = {
      ...account,
      // we do this here to avoid clearing the cache
      can_limited_fund: countryUtil.canLimitedFund(
        details.country_of_tax_residence ?? "",
      ),
    };

    // default step
    let step = getStep(clone?.last_onboarding_step ?? "");

    // has not completed the first step
    if (!getHasItem("account_type_step")) {
      step = getStep("account_type_step");
    }

    // v1 user and has completed the first step
    if (!step && getHasItem("account_type_step")) {
      step = getStep("signup_step");
    }

    const fpslEligible = isFpslEligible(details, countryUtil.infos);

    // Both stockLendingStep and disclosuresStep map to "financial_profile_step".
    // getStep() returns stockLendingStep (first match); skip to disclosuresStep
    // when ineligible or already completed in this session.
    //
    // When details are still fetching (stale from the previous Financial Profile
    // submission), we can't trust the eligibility check yet. Returning {} here
    // prevents StockLending from flashing briefly before the fresh data arrives.
    if (step === stockLendingStep && isDetailsFetching) {
      return {};
    }

    if (step === stockLendingStep && (!fpslEligible || fpslStepPassed)) {
      if (!fpslEligible) {
        clearFpslPreference(clone.id);
      }
      step = disclosuresStep;
    }

    // Cross-device resume: the user completed Disclosures on another device
    // (last_onboarding_step = disclosures_step → Review), but has no FPSL
    // preference in localStorage. Redirect back to StockLending so they can
    // answer the question before enrollment happens at Review.
    if (
      step === reviewStep &&
      fpslEligible &&
      getFpslPreference(clone.id) === null
    ) {
      step = stockLendingStep;
    }

    const visibleSteps = fpslEligible
      ? steps
      : steps.filter((s) => s !== stockLendingStep);

    const milestone = !step ? 0 : visibleSteps.indexOf(step) + 1;

    const next = () => {
      document.querySelector("#__anchor")?.scrollIntoView();
      setSeq(seq + 1);
      return true;
    };

    const completeFpslStep = (optIn: boolean) => {
      document.querySelector("#__anchor")?.scrollIntoView();
      setFpslPreference(clone.id, optIn);
      setFpslStepPassed(true);
    };

    const prev = (patch = true, callback?: CallableFunction) =>
      new Promise<boolean>((resolve, reject) => {
        document.querySelector("#__anchor")?.scrollIntoView();

        // When going back from Disclosures to StockLending, both share the
        // same backend step (financial_profile_step). We just reset the
        // frontend state — no backend call needed. Keep localStorage so
        // the user's radio selection is restored.
        if (step === disclosuresStep && fpslEligible) {
          setFpslStepPassed(false);
          callback?.();
          resolve(true);
          return;
        }

        // Note: we intentionally keep the FPSL localStorage preference when
        // going back from StockLending to Financial Profile. If the user
        // changes their financial data to become ineligible, the Review step
        // re-checks eligibility before enrolling and skips it automatically.

        if (milestone === 1 || !patch) {
          // if on first step
          milestone === 1 &&
            // refresh the page
            window.location.reload();

          // if callback is provided, call it
          callback?.();
          setSeq(seq + 1);
          resolve(true);
          return;
        }

        mutation.mutate(clone.id, {
          onError: reject,
          onSuccess: () => {
            callback?.();
            setSeq(seq + 1);
            resolve(true);
          },
        });
      });

    return {
      ready: true,
      milestone,
      visibleSteps,
      step,
      next,
      completeFpslStep,
      prev,
      details,
      // we've modified the account object
      account: clone,
    };
  }, [
    account,
    details,
    isDetailsFetching,
    countryUtil.isLoading,
    seq,
    fpslStepPassed,
  ]);

  // seq keeps track whenever we change pages
  // need to refetch account to get the latest onboarding step account property
  // biome-ignore lint/correctness/useExhaustiveDependencies: seq is a counter incremented by rerender() specifically to force this refetchAccount(), so it is the intended trigger even though the body never reads it. refetchAccount is unmemoized and would re-run the effect every render if listed.
  useEffect(() => {
    refetchAccount();
  }, [seq]);

  return {
    ...data,
    rerender,
    country: countryUtil,
    isLegalEntity,
  } as SessionData;
};
